import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Loader2, Send } from "lucide-react";
import { api, formatApiError } from "../../api";
import { TRAINER } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

// UX-TR-01 §1 SCR-P06-40: triggers the REAL Comms publish path (Prompt 07) for an assigned
// client — never a fabricated "sent!" toast. The response surfaces the actual consent-gate
// outcome (`whatsapp_allowed`/`block_reason`) so a trainer can see exactly why a message did or
// didn't go out on WhatsApp, matching the member's own opt-in/opt-out control.
export default function Message() {
  const reducedMotion = useReducedMotion();
  const [searchParams] = useSearchParams();
  const [clientId, setClientId] = useState(searchParams.get("client") || "");
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);

  const clientsQ = useQuery({ queryKey: ["trainer-clients"], queryFn: async () => (await api.get("/trainer/clients")).data });

  const sendMutation = useMutation({
    mutationFn: () => api.post(`/trainer/clients/${clientId}/message`, { text: text || undefined }),
    onSuccess: (res) => setResult(res.data),
    onError: (e) => setResult({ error: formatApiError(e).message }),
  });

  const clients = clientsQ.data?.clients || [];

  return (
    <div data-testid={TRAINER.messagePage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Message a client</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Sends a real WhatsApp message via the gym's Comms bus — gated by that client's own consent preferences.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        {clientsQ.isLoading ? (
          <div className="flex justify-center py-4"><Loader2 className="animate-spin" size={18} style={{ color: "#68686E" }} /></div>
        ) : (
          <Select onValueChange={setClientId} value={clientId}>
            <SelectTrigger data-testid={TRAINER.messageClientSelect}><SelectValue placeholder="Choose a client" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Textarea data-testid={TRAINER.messageTextInput} placeholder="Just checking in — see you at your next session!" value={text} onChange={(e) => setText(e.target.value)} rows={3} />
        <Button
          data-testid={TRAINER.messageSendButton}
          disabled={!clientId || sendMutation.isPending}
          onClick={() => { setResult(null); sendMutation.mutate(); }}
          className="w-full text-white gap-2" style={{ background: "#D6202B" }}
        >
          {sendMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <><Send size={13} /> Send</>}
        </Button>

        {result && (
          <div data-testid={TRAINER.messageResult} className="rounded-[8px] px-3 py-2.5 text-sm" style={{ background: "#F4F3EF" }}>
            {result.error ? (
              <span style={{ color: "var(--scarlet-neg)" }}>{result.error}</span>
            ) : result.whatsapp_allowed ? (
              <span style={{ color: "var(--scarlet-pos)" }}>Sent — this client has WhatsApp reminders enabled.</span>
            ) : (
              <span style={{ color: "#68686E" }}>Not delivered on WhatsApp — this client hasn't opted in (reason: {result.block_reason}). Logged in-app instead.</span>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}
